// The lead magnet watch page (/free/<slug>/watch?t=...) carries a per-viewer signed
// token in its address. The page removes it with history.replaceState, but Sentry has
// already recorded the original address: navigation breadcrumbs keep data.from and
// data.to with the query, and request.url, transaction names and span data can carry
// it too. Any error in that tab would then send the token to Sentry.
//
// Since 2026-11-02 the DM guide links carry `dm=<ref>` (lib/dm-ref.ts), a per-person id
// that must not reach Sentry either, so it is removed the same way.
//
// These helpers remove the `t` query parameter from every string in an event or
// breadcrumb before it leaves the browser. Plain regexes only: no lookbehind, because
// this file loads on every page and Safari below iOS 16.4 cannot parse one
// (2026-10-25, the old-iPhone outage).

const TOKEN_PARAM = /([?&])(?:t|dm)=[^&#\s"'<>]*&?/g;
const SCRUBBED_KEYS = ["t", "dm"];

/** Removes the `t` query parameter from any URL-shaped text in a string. */
export function scrubTokenFromString(value: string): string {
  if (value.indexOf("t=") === -1 && value.indexOf("dm=") === -1) return value;
  return value
    .replace(TOKEN_PARAM, "$1")
    .replace(/[?&](?=#|\s|$|["'<>])/g, "")
    .replace(/&(?=#)/g, "");
}

/** Removes `t` from a bare query string such as "t=abc&x=1" (no leading "?"). */
function scrubQueryString(value: string): string {
  return scrubTokenFromString(`?${value}`).replace(/^\?/, "");
}

const MAX_DEPTH = 12;

function scrubDeep(value: unknown, depth: number, seen: WeakSet<object>): unknown {
  if (typeof value === "string") return scrubTokenFromString(value);
  if (value === null || typeof value !== "object" || depth > MAX_DEPTH) return value;
  if (seen.has(value)) return value;
  seen.add(value);
  if (Array.isArray(value)) {
    for (let i = 0; i < value.length; i++) value[i] = scrubDeep(value[i], depth + 1, seen);
    return value;
  }
  const record = value as Record<string, unknown>;
  for (const key of Object.keys(record)) {
    const current = record[key];
    if (key === "query_string") {
      if (typeof current === "string") {
        record[key] = scrubQueryString(current);
        continue;
      }
      if (Array.isArray(current)) {
        record[key] = current.filter(
          (pair) => !(Array.isArray(pair) && SCRUBBED_KEYS.includes(pair[0])),
        );
        continue;
      }
      if (current && typeof current === "object") {
        for (const k of SCRUBBED_KEYS) delete (current as Record<string, unknown>)[k];
      }
    }
    record[key] = scrubDeep(current, depth + 1, seen);
  }
  return value;
}

/** Scrubs the token from every string in a Sentry event or breadcrumb, in place. */
export function scrubWatchToken<T>(payload: T): T {
  return scrubDeep(payload, 0, new WeakSet()) as T;
}
